/**
 * Pure helpers for staging student review edit form mapping (no React / API).
 */

import { parsePersonName } from '../domain/identity/parsePersonName';

export const EMPTY_STAGING_EDIT_FORM = {
  email: '',
  firstName: '',
  middleName: '',
  lastName: '',
  phoneNumber: '',
  gender: '',
  employeeId: '',
  currentAddress: '',
  currentLocation: '',
  pincode: '',
  parentsName: '',
  parentsPhone: '',
  batchId: '',
};

export function fieldFromPayload(payload, keys) {
  if (!payload || typeof payload !== 'object') return '';
  for (const key of keys) {
    if (payload[key] != null && String(payload[key]).trim() !== '') {
      return String(payload[key]).trim();
    }
  }
  return '';
}

/** Build initial form values from a staging document. */
export function buildStagingEditForm(doc) {
  const rr = doc?.ruleResult || {};
  const patch = rr.studentPatch || {};
  const payload = doc?.payload || {};
  const fullName = fieldFromPayload(payload, ['Name', 'fullName', 'Full Name', 'name']);
  const parsed = parsePersonName({
    firstName: rr.firstName || fieldFromPayload(payload, ['firstName', 'First Name']),
    middleName: rr.middleName || fieldFromPayload(payload, ['middleName', 'Middle Name', 'Middle']),
    lastName: rr.lastName || fieldFromPayload(payload, ['lastName', 'Last Name']),
    fullName,
  });
  return {
    email:
      rr.email ||
      doc?.emailKey ||
      fieldFromPayload(payload, ['email', 'Email Address', 'emailAddress']) ||
      '',
    firstName: parsed.firstName,
    middleName: parsed.middleName,
    lastName: parsed.lastName,
    phoneNumber:
      patch.phoneNumber ||
      fieldFromPayload(payload, ['phoneNumber', 'Phone Number', 'mobile', 'phone']) ||
      '',
    gender: patch.gender || fieldFromPayload(payload, ['gender', 'Gender']) || '',
    employeeId:
      patch.employeeId ||
      fieldFromPayload(payload, ['employeeId', 'veStudentId', 'employee_id']) ||
      '',
    currentAddress:
      patch.currentAddress ||
      fieldFromPayload(payload, ['currentAddress', 'address', 'Address']) ||
      '',
    currentLocation:
      patch.currentLocation ||
      fieldFromPayload(payload, ['currentLocation', 'current_location']) ||
      '',
    pincode: patch.pincode || fieldFromPayload(payload, ['pincode', 'Pincode']) || '',
    parentsName:
      patch.parentsName ||
      fieldFromPayload(payload, ['parentsName', 'guardianName']) ||
      '',
    parentsPhone:
      patch.parentsPhone ||
      fieldFromPayload(payload, ['parentsPhone', 'guardianContact']) ||
      '',
    batchId: rr.enrollment?.batchId
      ? String(rr.enrollment.batchId)
      : fieldFromPayload(payload, ['batchId', 'Batch ID']) || '',
  };
}

/** Default enrollment radio/select from rule result. */
export function resolveEnrollmentDecision(doc) {
  const rr = doc?.ruleResult || {};
  const recommended = rr.multiRowGuidance?.recommendedEnrollment;
  // Without a concrete batch id, default to skip so identity saves do not FK-fail
  if (!rr.enrollment?.batchId) return 'skip';
  if (recommended === 'create' || recommended === 'update' || recommended === 'skip') {
    return recommended;
  }
  if (rr.enrollment?.action === 'conflict') return 'skip';
  if (rr.enrollment?.action === 'update') return 'update';
  if (rr.reviewReasons?.includes('new_enrollment_for_existing_student')) return 'create';
  if (rr.reviewReasons?.includes('duplicate_email_in_file')) return 'create';
  if (rr.matchStatus === 'existing') return 'skip';
  return 'create';
}

/**
 * Client-side checks before decide/apply. Returns error message or null.
 */
export function validateStagingEditForm(form, enrollmentDecision) {
  const email = String(form?.email || '').trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid email before saving';
  }
  if (!String(form?.firstName || '').trim() || !String(form?.lastName || '').trim()) {
    return 'First name and last name are required';
  }
  const batchId = String(form?.batchId || '').trim();
  if (
    (enrollmentDecision === 'create' || enrollmentDecision === 'update') &&
    batchId &&
    !/^\d+$/.test(batchId)
  ) {
    return 'Batch ID must be a number (or clear it and set Enrollment to Skip)';
  }
  return null;
}

/** Resolve enrollment decision for API: empty batch → skip. */
export function effectiveEnrollmentDecision(form, enrollmentDecision) {
  const batchId = String(form?.batchId || '').trim();
  if (!batchId) return 'skip';
  return enrollmentDecision || 'skip';
}

/** Map form fields to API decide overrides (omit empties; batchId always sent). */
export function buildDecideOverrides(form) {
  return {
    email: form.email || undefined,
    firstName: form.firstName || undefined,
    middleName: form.middleName || undefined,
    lastName: form.lastName || undefined,
    phoneNumber: form.phoneNumber || undefined,
    gender: form.gender || undefined,
    employeeId: form.employeeId || undefined,
    currentAddress: form.currentAddress || undefined,
    currentLocation: form.currentLocation || undefined,
    pincode: form.pincode || undefined,
    parentsName: form.parentsName || undefined,
    parentsPhone: form.parentsPhone || undefined,
    // Always include so clearing the field removes a stale rule-engine batchId
    batchId: form.batchId != null ? String(form.batchId).trim() : '',
  };
}

/** Redact password-like keys for display JSON. */
export function redactPayloadPasswords(payload) {
  const p = { ...(payload || {}) };
  for (const key of Object.keys(p)) {
    if (/password/i.test(key) && p[key]) p[key] = '••••••••';
  }
  return p;
}
